import {useCallback, useMemo, useState} from 'react'

import type {PinControls} from '../components/views/PinViewMenuItem'
import type {SavedView} from '../components/views/types'
import {isPinLimitError, pinnedMutationErrorMessage, type SavedViewMutationOptions} from '../utils/saved-view-mutations'
import {usePinnedSavedViews} from './use-pinned-saved-views'
import type {SavedViewPinSource} from './use-saved-view-analytics'
import {useSetSavedViewPinnedMutation} from './use-set-saved-view-pinned-mutation'

export interface SavedViewPinControlsResult {
  pinControls?: PinControls
  pinErrorMessage: string | null
  retryPin?: () => void
  isPinRetrying: boolean
}

interface PinFailure {
  error: Error
  id: number
  name: string
  pinned: boolean
}

export function useSavedViewPinControls({
  owner,
  repo,
  enabled,
  source,
}: SavedViewMutationOptions & {enabled: boolean; source: SavedViewPinSource}): SavedViewPinControlsResult {
  const [failure, setFailure] = useState<PinFailure | null>(null)
  const [retryingId, setRetryingId] = useState<number | null>(null)

  const clearFailureFor = useCallback((id: number) => {
    setFailure(prev => (prev?.id === id ? null : prev))
  }, [])

  const {mutate, mutateAsync} = useSetSavedViewPinnedMutation({
    owner,
    repo,
    source,
    onPinMutate: ({id}) => clearFailureFor(id),
    onPinSuccess: ({id}) => clearFailureFor(id),
    onPinError: ({error, id, pinned, name}) => setFailure({error, id, pinned, name}),
  })

  const {pinnedCount, isFetching} = usePinnedSavedViews({owner, repo, enabled})
  const pinLimitLoading = enabled && pinnedCount === undefined && isFetching

  const togglePin = useCallback(
    (view: SavedView) => {
      mutate({id: view.id, name: view.name, pinned: !view.pinned})
    },
    [mutate],
  )

  const retryPin = useCallback(() => {
    if (!failure) return
    const {id, name, pinned} = failure
    setRetryingId(id)
    const clearRetry = () => setRetryingId(prev => (prev === id ? null : prev))
    async function runRetry() {
      try {
        await mutateAsync({id, name, pinned})
      } catch {
        clearRetry()
        return
      }
      clearRetry()
    }
    void runRetry()
  }, [mutateAsync, failure])

  const pinControls = useMemo<PinControls | undefined>(
    () => (enabled ? {togglePin, pinnedCount, pinLimitLoading} : undefined),
    [enabled, togglePin, pinnedCount, pinLimitLoading],
  )

  const canRetry = failure != null && !isPinLimitError(failure.error, failure.pinned)
  const pinErrorMessage = failure ? pinnedMutationErrorMessage(failure.error, failure.pinned, failure.name) : null
  const isPinRetrying = retryingId != null && (failure == null || failure.id === retryingId)
  const showRetry = canRetry || isPinRetrying

  return {pinControls, pinErrorMessage, retryPin: showRetry ? retryPin : undefined, isPinRetrying}
}
